import { useState, useEffect, useCallback } from "react";
import { useAuth } from "./useAuth";
import {
  getWatchedMovies,
  addWatchedMovie,
  addViewingToMovie,
  deleteWatchedMovie,
  updateWatchedMovie,
  isMovieWatched,
  getMovieViewings,
  deleteViewing,
  getWatchlist,
  addToWatchlist,
  removeFromWatchlist,
  isInWatchlist,
  getUserStats,
} from "../services/movies";

export const useWatchedMovies = () => {
  const { user } = useAuth();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMovies = useCallback(async () => {
    if (!user) {
      setMovies([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getWatchedMovies(user.uid);
      setMovies(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchMovies();
  }, [fetchMovies]);

  const addMovie = async (movieData, viewing, friendsToSync = []) => {
    try {
      await addWatchedMovie(user.uid, movieData, viewing, friendsToSync);
      await fetchMovies();
    } catch (err) {
      throw err;
    }
  };

  const addViewing = async (movieDocId, viewing, friendsToSync = []) => {
    try {
      // Get the movie to get tmdbId
      const movie = movies.find((m) => m.id === movieDocId);
      if (movie) {
        await addViewingToMovie(movieDocId, user.uid, movie.tmdbId, viewing, friendsToSync);
      }
    } catch (err) {
      throw err;
    }
  };

  const removeMovie = async (movieId) => {
    try {
      await deleteWatchedMovie(movieId);
      setMovies((prev) => prev.filter((m) => m.id !== movieId));
    } catch (err) {
      throw err;
    }
  };

  const updateMovie = async (movieId, updates) => {
    try {
      await updateWatchedMovie(movieId, updates);
      await fetchMovies();
    } catch (err) {
      throw err;
    }
  };

  const checkIfWatched = async (tmdbId) => {
    if (!user) return null;
    return await isMovieWatched(user.uid, tmdbId);
  };

  const getViewings = async (tmdbId) => {
    if (!user) return [];
    return await getMovieViewings(user.uid, tmdbId);
  };

  const removeViewing = async (viewingId, movieDocId) => {
    try {
      const result = await deleteViewing(viewingId, movieDocId);
      if (result.movieDeleted) {
        // Refresh movie list since the movie was removed
        await fetchMovies();
      }
      return result;
    } catch (err) {
      throw err;
    }
  };

  return {
    movies,
    loading,
    error,
    addMovie,
    addViewing,
    removeMovie,
    removeViewing,
    updateMovie,
    checkIfWatched,
    getMovieViewings: getViewings,
    refresh: fetchMovies,
  };
};

export const useWatchlist = () => {
  const { user } = useAuth();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWatchlist = useCallback(async () => {
    if (!user) {
      setMovies([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getWatchlist(user.uid);
      setMovies(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  const addMovie = async (movieData, friendsToSync = []) => {
    try {
      await addToWatchlist(user.uid, movieData, friendsToSync);
      await fetchWatchlist();
    } catch (err) {
      throw err;
    }
  };

  const removeMovie = async (watchlistItemId) => {
    try {
      await removeFromWatchlist(watchlistItemId);
      setMovies((prev) => prev.filter((m) => m.id !== watchlistItemId));
    } catch (err) {
      throw err;
    }
  };

  const checkIfInWatchlist = async (tmdbId) => {
    if (!user) return null;
    return await isInWatchlist(user.uid, tmdbId);
  };

  return {
    movies,
    loading,
    error,
    addMovie,
    removeMovie,
    checkIfInWatchlist,
    refresh: fetchWatchlist,
  };
};

export const useUserStats = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const data = await getUserStats(user.uid);
        setStats(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user]);

  return { stats, loading, error };
};
